
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, Gift } from 'lucide-react';
import { cn } from '@/lib/utils';
import ReserveButton from '../ReserveButton';
import { 
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

interface DesktopNavigationProps {
  toggleSearch: () => void;
  isSearchOpen: boolean;
}

const DesktopNavigation: React.FC<DesktopNavigationProps> = ({
  toggleSearch,
  isSearchOpen
}) => {
  const location = useLocation();

  const tourLinks = [
    { name: 'Brooklyn Heights', path: '/tours/brooklyn-heights' },
    { name: 'Financial District', path: '/tours/financial-district' },
    { name: 'Lower East Side', path: '/tours/lower-east-side' },
    { name: 'Private Tours', path: '/tours/private-tours' },
  ];

  return (
    <div className="hidden md:flex items-center gap-1 lg:gap-6">
      <Link 
        to="/about"
        className={cn(
          'px-3 py-2 font-montserrat font-medium transition-all duration-300 relative group',
          location.pathname === '/about' 
            ? "text-[#657C56] font-semibold" 
            : "text-[#3E2723] hover:text-[#657C56]"
        )}
        aria-current={location.pathname === '/about' ? 'page' : undefined}
      >
        About
        <span 
          className={cn(
            "absolute left-0 bottom-0 w-full h-[1px] transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100",
            location.pathname === '/about' ? "bg-[#657C56] scale-x-100" : "bg-[#657C56]"
          )}
          aria-hidden="true"
        ></span>
      </Link>
      
      {/* Tours Navigation Menu */}
      <NavigationMenu>
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuTrigger 
              className={cn(
                'px-3 py-2 font-montserrat font-medium transition-all duration-300 relative group bg-transparent hover:bg-transparent data-[state=open]:bg-transparent text-base h-auto',
                location.pathname.startsWith('/tours') 
                  ? "text-[#657C56] font-semibold" 
                  : "text-[#3E2723] hover:text-[#657C56]"
              )}
            >
              <Link 
                to="/tours"
                className="flex items-center"
                onClick={(e) => e.stopPropagation()}
              >
                Tours
              </Link>
              <span 
                className={cn(
                  "absolute left-0 bottom-0 w-full h-[1px] transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100",
                  location.pathname.startsWith('/tours') ? "bg-[#657C56] scale-x-100" : "bg-[#657C56]"
                )}
                aria-hidden="true"
              ></span>
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <div className="w-64 p-4 bg-white shadow-lg border rounded-md">
                <div className="space-y-2">
                  {tourLinks.map((tour) => (
                    <NavigationMenuLink key={tour.path} asChild>
                      <Link
                        to={tour.path}
                        className={cn(
                          "block px-3 py-2 rounded-md font-montserrat transition-colors duration-200",
                          location.pathname === tour.path
                            ? "text-[#657C56] bg-[#FDF6ED] font-semibold"
                            : "text-[#3E2723] hover:text-[#657C56] hover:bg-[#FDF6ED]"
                        )}
                      >
                        {tour.name}
                      </Link>
                    </NavigationMenuLink>
                  ))}
                  <div className="border-t border-[#C58B65]/20 my-2"></div>
                  <NavigationMenuLink asChild>
                    <a
                      href="https://fareharbor.com/embeds/book/catsabouttown/items/562633/calendar/?full-items=yes&flow=1219724"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-3 py-2 rounded-md font-montserrat transition-colors duration-200 text-[#3E2723] hover:text-[#657C56] hover:bg-[#FDF6ED] flex items-center"
                    >
                      <Gift className="mr-2 h-4 w-4" />
                      Gift Cards
                    </a>
                  </NavigationMenuLink>
                </div>
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>

      <Link 
        to="/gallery"
        className={cn(
          'px-3 py-2 font-montserrat font-medium transition-all duration-300 relative group',
          location.pathname === '/gallery' 
            ? "text-[#657C56] font-semibold" 
            : "text-[#3E2723] hover:text-[#657C56]"
        )}
        aria-current={location.pathname === '/gallery' ? 'page' : undefined}
      >
        Gallery
        <span 
          className={cn(
            "absolute left-0 bottom-0 w-full h-[1px] transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100",
            location.pathname === '/gallery' ? "bg-[#657C56] scale-x-100" : "bg-[#657C56]"
          )}
          aria-hidden="true"
        ></span>
      </Link>

      <Link 
        to="/blog"
        className={cn(
          'px-3 py-2 font-montserrat font-medium transition-all duration-300 relative group',
          location.pathname === '/blog' 
            ? "text-[#657C56] font-semibold" 
            : "text-[#3E2723] hover:text-[#657C56]"
        )}
        aria-current={location.pathname === '/blog' ? 'page' : undefined}
      >
        Blog
        <span 
          className={cn(
            "absolute left-0 bottom-0 w-full h-[1px] transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100",
            location.pathname === '/blog' ? "bg-[#657C56] scale-x-100" : "bg-[#657C56]"
          )}
          aria-hidden="true"
        ></span>
      </Link>

      <Link 
        to="/in-the-news"
        className={cn(
          'px-3 py-2 font-montserrat font-medium transition-all duration-300 relative group',
          location.pathname === '/in-the-news' 
            ? "text-[#657C56] font-semibold" 
            : "text-[#3E2723] hover:text-[#657C56]"
        )}
        aria-current={location.pathname === '/in-the-news' ? 'page' : undefined}
      >
        In the News
        <span 
          className={cn(
            "absolute left-0 bottom-0 w-full h-[1px] transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100",
            location.pathname === '/in-the-news' ? "bg-[#657C56] scale-x-100" : "bg-[#657C56]"
          )}
          aria-hidden="true"
        ></span>
      </Link>

      <Link 
        to="/contact"
        className={cn(
          'px-3 py-2 font-montserrat font-medium transition-all duration-300 relative group',
          location.pathname === '/contact' 
            ? "text-[#657C56] font-semibold" 
            : "text-[#3E2723] hover:text-[#657C56]"
        )}
        aria-current={location.pathname === '/contact' ? 'page' : undefined}
      >
        Contact
        <span 
          className={cn(
            "absolute left-0 bottom-0 w-full h-[1px] transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100",
            location.pathname === '/contact' ? "bg-[#657C56] scale-x-100" : "bg-[#657C56]"
          )}
          aria-hidden="true"
        ></span>
      </Link>

      <button
        onClick={toggleSearch}
        className="p-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[#657C56] text-[#3E2723] hover:text-[#657C56]"
        aria-expanded={isSearchOpen}
        aria-label="Search website"
      >
        <Search size={20} aria-hidden="true" />
      </button>
      <ReserveButton 
        className="ml-4"
        showCalendarIcon={false}
      />
    </div>
  );
};

export default DesktopNavigation;
